/**
 * EVN Archive — Fit Filter
 * ------------------------
 * Filters and re-sorts a collection grid to match a saved fit profile,
 * activated by a `?fit=1` URL param (set by the homepage "Find your fit"
 * CTA after it saves a profile via the shared engine — see
 * snippets/fit-finder.liquid's pendingRedirect handling).
 *
 * Runs entirely off data already on the page: each product-card (see
 * snippets/product-card.liquid) carries its own custom.size_and_fit_rich
 * data in a <script class="product-fit-data"> tag, parsed with the exact
 * same window.EVNFitFinder engine that powers the PDP widget — so a
 * product's fit category here is identical to what it'd show on its own
 * page, nothing re-implemented.
 *
 * As of Sept 2026 every active product has Size & Fit data set, so in
 * practice every card gets a real rank — the "unknown fit" fallback below
 * exists as a safety net for any future product launched before its Size &
 * Fit copy is written, not because it's needed today.
 *
 * Too small / too big → hidden. Everything else ordered: recommended, then
 * roomy ("relaxed fit" in customer-facing copy — same category, matches
 * the PDP widget's wording), then snug. Unknown-fit products (see above)
 * are left visible and unranked, appended after the sized ones.
 */
(function () {
  'use strict';

  function getFitParam() {
    try {
      return new URLSearchParams(window.location.search).get('fit') === '1';
    } catch (e) {
      return false;
    }
  }

  if (!getFitParam()) return;

  var RANK = { recommended: 0, roomy: 1, snug: 2 };
  var UNKNOWN_RANK = 3;
  var HIDE_CATEGORIES = { too_small: true, too_big: true };

  function whenEngineReady(callback) {
    if (window.EVNFitFinder) { callback(); return; }
    var tries = 0;
    var interval = setInterval(function () {
      tries += 1;
      if (window.EVNFitFinder) {
        clearInterval(interval);
        callback();
      } else if (tries > 40) {
        clearInterval(interval);
        console.warn('Fit filter: engine script did not load in time.');
      }
    }, 50);
  }

  function insertBanner(grid) {
    if (document.querySelector('[data-fit-filter-banner]')) return;
    var banner = document.createElement('div');
    banner.setAttribute('data-fit-filter-banner', '');
    var clearUrl = new URL(window.location.href);
    clearUrl.searchParams.delete('fit');
    // Pill shape (fully rounded) and a sharp-edged checkmark (stroke-linecap:
    // square, stroke-linejoin: miter) rather than the theme's own
    // icon-checkmark.svg, which uses round caps/joins — the same path,
    // redrawn with sharp ends to match the site's square-cornered visual
    // language (0-radius buttons, 2px badge corners) instead of introducing
    // a soft/curved mark that's inconsistent with everything else on site.
    banner.style.cssText =
      'display:inline-flex;align-items:center;gap:8px;' +
      'margin:16px 24px 4px;padding:8px 16px;' +
      'background:#f4f4f4;border:1px solid #d3cec5;border-radius:999px;' +
      'font-size:13px;color:#030302;';
    banner.innerHTML =
      '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0;">' +
        '<path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="miter"/>' +
      '</svg>' +
      '<span>Showing pieces recommended for you.</span>' +
      '<a href="' + clearUrl.pathname + clearUrl.search + '" style="text-decoration:underline;color:inherit;">Clear</a>';
    grid.parentElement.insertBefore(banner, grid);
  }

  function rankFor(li, profile) {
    var script = li.querySelector('script.product-fit-data');
    if (!script) return UNKNOWN_RANK;
    var raw;
    try {
      raw = JSON.parse(script.textContent);
    } catch (e) {
      return UNKNOWN_RANK;
    }
    if (!raw) return UNKNOWN_RANK;

    var Engine = window.EVNFitFinder;
    var productData = Engine.parseProductFitData(raw);
    var rec = Engine.recommendSize(productData, profile);
    if (!rec) return UNKNOWN_RANK;
    if (HIDE_CATEGORIES[rec.category]) return -1; // signals "hide"
    return RANK.hasOwnProperty(rec.category) ? RANK[rec.category] : UNKNOWN_RANK;
  }

  function processGrid(grid, profile) {
    var items = grid.querySelectorAll('li.product-grid__item[data-product-id]:not([data-fit-processed])');
    if (!items.length) return;

    items.forEach(function (li) {
      var rank = rankFor(li, profile);
      li.setAttribute('data-fit-processed', 'true');
      if (rank === -1) {
        li.hidden = true;
      } else {
        li.setAttribute('data-fit-rank', String(rank));
      }
    });

    // Re-sort all currently-visible, already-processed items by rank —
    // appendChild on an existing child moves it, so this reorders in place
    // without removing/recreating any node (preserves event listeners,
    // image loading state, etc.) — matters for infinite scroll, where
    // items already on screen shouldn't flicker or reload when a new
    // batch arrives and gets merged into the sort.
    var visible = Array.prototype.slice
      .call(grid.querySelectorAll('li.product-grid__item[data-fit-rank]'))
      .sort(function (a, b) {
        return Number(a.getAttribute('data-fit-rank')) - Number(b.getAttribute('data-fit-rank'));
      });
    visible.forEach(function (li) { grid.appendChild(li); });
  }

  whenEngineReady(function () {
    var Engine = window.EVNFitFinder;
    var profile = Engine.loadProfile();
    if (!profile) return; // arrived via ?fit=1 with no saved profile — nothing to filter against

    var grid = document.querySelector('.product-grid');
    if (!grid) return;

    insertBanner(grid);
    processGrid(grid, profile);

    // Infinite scroll appends new <li> batches into the same .product-grid
    // (see results-list.js) regardless of which collection or filter state
    // is active — this observer catches every batch as it lands and
    // reprocesses only the newly-added, not-yet-ranked items, so the fit
    // filter keeps applying no matter how many pages the customer scrolls.
    var observer = new MutationObserver(function () {
      processGrid(grid, profile);
    });
    observer.observe(grid, { childList: true });
  });
})();
